import type { ExerciseSchema } from '~/types/schema';
import { getServices } from '~/db/services';
import { cleanRecommendationItemsSegmented } from '~/lib/recommendation/clean';
import {
  getMapRecommendationParametersFromItems,
  getRecommendationLevelItemsByExercises,
  getSortedAndCleanedScoreRecommendations,
  saveBaseInfosRecommendationParameters,
} from '~/lib/recommendation';

export type UseRecommendation = {
  load: () => Promise<void>;
  recommendedItems: Ref<BaseRecommendationSchema[]>;
  mMedia: Ref<Map<string, MediaSchema>>;
  score: ComputedRef<number>;
  mScoresForRecommendedItems: Ref<Map<string, number>>;
  exercisesWithErrors: ComputedRef<
    { exercise: ExerciseSchema; score: number }[]
  >;
};

export const INJECT_RECOMMENDATION = Symbol(
  'useCategory composable'
) as InjectionKey<UseRecommendation>;

export const useRecommendation = (): UseRecommendation => {
  const { $db } = useNuxtApp();
  const { recommendationsService, mediasService } = getServices($db);

  const { isModal } = useModalRouter();

  const { categoryManager, disciplineId } = inject(INJECT_CATEGORY_PAGE);

  const { segmentedIds } = useContents();

  const profile = useProfileStore();

  const recommendedItems = ref<BaseRecommendationSchema[]>([]);

  const mScoresForRecommendedItems = ref<Map<string, number>>(new Map());

  const mMedia = ref<Map<string, MediaSchema>>();
  mMedia.value = new Map();

  const oExercises = computed(() => {
    return arrayToObject(categoryManager.category.value.exercises);
  });

  const { progressions } = inject(INJECT_PROGRESSION_EXERCISES);

  const completedExerciseIds = computed(() => {
    return progressions.value.reduce((ids, progression) => {
      const exerciseProgression =
        progression.data as ExerciseDetailsProgression;
      if (exerciseProgression.status === 'done') {
        ids.push(progression.contentId);
      }
      return ids;
    }, []);
  });

  const { score } = useExercisesChapterProgressions(
    progressions,
    completedExerciseIds
  );

  const progressionInfosByExercises = computed(() => {
    return progressions.value.reduce((map, progression) => {
      const exerciseProgression =
        progression.data as ExerciseDetailsProgression;
      if (
        !exerciseProgression.maxPoints ||
        exerciseProgression.status !== 'done'
      )
        return map;
      const score = exerciseProgression.points / exerciseProgression.maxPoints;
      map.set(progression.contentId, {
        score,
        userUpdatedAt: progression.userUpdatedAt,
      });
      return map;
    }, new Map<string, { score: number; userUpdatedAt: string }>());
  });

  const exercisesWithErrors = computed(() => {
    const mExercisesWithScore = progressions.value.reduce(
      (map, progression) => {
        const exerciseProgression =
          progression.data as ExerciseDetailsProgression;
        if (!exerciseProgression.maxPoints) return map;
        const score =
          exerciseProgression.points / exerciseProgression.maxPoints;
        if (score < 1) {
          map.set(progression.contentId, {
            score,
            exercise: oExercises.value[progression.contentId],
          });
        }
        return map;
      },
      new Map<string, { score: number; exercise: ExerciseSchema }>()
    );

    return Array.from(mExercisesWithScore.values());
  });

  watch(
    () => isModal.value,
    (to: boolean, from: boolean) => {
      if (from && !to) {
        load();
      }
    }
  );

  const load = async () => {
    if (!profile.featureFlags.adaptive) return;

    const recommendations = await recommendationsService.findByExerciseIds(
      completedExerciseIds.value
    );

    let recommendedItemsTemp = [];

    recommendedItemsTemp = getRecommendationLevelItemsByExercises(
      recommendations,
      progressionInfosByExercises.value,
      profile.adaptiveParameters.gradeThresholds
    );

    recommendedItemsTemp = await cleanRecommendationItemsSegmented(
      $db,
      recommendedItemsTemp,
      segmentedIds.value
    );

    const mRecommendationParameters =
      await getMapRecommendationParametersFromItems($db, recommendedItemsTemp);

    const scoreRecommendations = getSortedAndCleanedScoreRecommendations(
      recommendedItemsTemp,
      mRecommendationParameters,
      profile.adaptiveParameters
    );

    recommendedItemsTemp = scoreRecommendations.map(
      (item) => item.recommendationItem
    );

    recommendedItemsTemp.forEach((item) => {
      mMedia.value.set(item.mediaIconId, null);
    });

    const medias = await mediasService.findByIds(
      Array.from(mMedia.value.keys())
    );

    medias.forEach((media) => mMedia.value.set(media.id, media));

    recommendedItems.value = recommendedItemsTemp;

    scoreRecommendations.forEach((item) =>
      mScoresForRecommendedItems.value.set(
        item.recommendationItem.id,
        item.scores.recommendationScore
      )
    );

    saveBaseInfosRecommendationParameters(
      $db,
      scoreRecommendations,
      disciplineId.value
    );
  };

  return {
    load,
    recommendedItems,
    mScoresForRecommendedItems,
    mMedia,
    exercisesWithErrors,
    score,
  };
};
